/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import jsonExport from "jsonexport/dist";
import classnames from "classnames";

import { useMediaQuery, Grid, Tooltip, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  AutocompleteInput,
  BulkActionsToolbar,
  Button,
  Confirm,
  Datagrid,
  downloadCSV,
  FilterButton,
  FunctionField,
  ListContextProvider,
  ListToolbar,
  NumberField,
  ReferenceField,
  SelectField,
  SelectInput,
  TextField,
  TopToolbar,
  useList,
  useListContext,
  useNotify,
} from "react-admin";
import { useFormState } from "react-final-form";

import {
  ChevronRightIcon,
  CreateIcon,
  DeleteIcon,
  EditIcon,
  ExportIcon,
  WarningIcon,
} from "../../../../components/common/iconography";
import { Pagination } from "../../../../components/Pagination";
import SimpleList from "../../../../components/common/SimpleList";

import {
  isSubscriptionDisabled,
  logError,
  logHistory,
} from "../../../../utils/appUtils";
import { deepRedirect, formatCurrency } from "../../../../utils/utils";
import {
  CarrierTypes,
  ChargeRuleHeaderMap,
  ResourceType,
} from "../../../../utils/constants";

import { useDeleteButtonStyles } from "../../../../components/common/useDeleteButtonStyles";

import { getUnitPrice } from "../../services/ServiceUtils";
import {
  ChargeEventChoices,
  StorageFrequencyChoices,
  FeeAccrualChoices,
  FreeDaysPolicy,
} from "./chargeRuleConstants";
import { ExecutionIcon } from "./ExecutionIcon";
import {
  fetchChargeRules,
  getChargeRuleLabel,
  softDeleteChargeRules,
  validateChargeRule,
} from "./ChargeRuleUtils";
import { ImportChargeRulesMenuButton } from "./ImportChargeRulesMenuButton";
import {
  fetchCarrierEquipmentTypes,
  getRailEquipmentTypes,
  getTruckEquipmentTypes,
} from "../../../../utils/loadTypeUtils";

const useStyles = makeStyles((theme) => ({
  actionButton: {
    marginLeft: theme.spacing(1),
  },
}));

const exportList = (chargeRules, account) => {
  const fileNamePrefix = account.displayName || account.businessName;

  const chargeRulesForExport = chargeRules.map((rule) => ({
    event: rule.event,
    serviceName: rule.service?.name || "MISSING", // Export service name instead of ID
    billingUOM: rule.billingUOM,
    carrierType: rule.carrierType,
    reloadYardCommodityId: rule.reloadYardCommodityId,
    areaId: rule.areaId,
    zoneId: rule.zoneId,
    minCharge: rule.minCharge,
    maxCharge: rule.maxCharge,
    frequency: rule.frequency,
    feeAccrual: rule.feeAccrual,
    graduatedRates: JSON.stringify(rule.graduatedRates || []),
    freeDaysPolicy: rule.freeDaysPolicy,
    freeDays: rule.freeDays,
    freeCars: rule.freeCars,
    execution: rule.execution,
  }));

  jsonExport(
    chargeRulesForExport,
    {
      rename: Object.keys(ChargeRuleHeaderMap),
    },
    (err, csv) => {
      downloadCSV(csv, `${fileNamePrefix}_charge_rules`);
    },
  );
};

const getListFilters = (allServices, xsDown) => [
  <SelectInput
    source="event"
    label="Event Type"
    choices={ChargeEventChoices}
    alwaysOn
    resettable
    variant="outlined"
  />,
  <AutocompleteInput
    source="serviceId"
    label="Service"
    choices={allServices ? allServices : []}
    alwaysOn={!xsDown}
    resettable
    variant="outlined"
  />,
];

const ListActions = (props) => {
  const classes = useStyles();
  const {
    account,
    allChargeRules,
    allServices,
    backPath,
    history,
    formState,
    openConfirm,
    hasCreate,
    xsDown,
    handleRefresh,
  } = props;

  const handleCreate = () => {
    if (formState.dirty)
      openConfirm("/chargeRules/create", { account, allChargeRules });
    else
      deepRedirect(history, "/chargeRules/create", backPath, {
        account,
        allChargeRules,
      });
  };

  return (
    <TopToolbar>
      {!xsDown && <FilterButton />}
      {hasCreate && (
        <Button
          label="Create"
          color="primary"
          className={classes.actionButton}
          onClick={handleCreate}
          disabled={isSubscriptionDisabled() || allServices.length === 0}
        >
          <CreateIcon />
        </Button>
      )}
      {!xsDown && (
        <>
          <ImportChargeRulesMenuButton
            account={account}
            existingRules={allChargeRules}
            existingServices={allServices}
            handleRefresh={handleRefresh}
            disabled={isSubscriptionDisabled() || allServices.length === 0}
          />
          <Button
            label="Export"
            size="small"
            color="primary"
            onClick={() => exportList(allChargeRules, account)}
            disabled={allChargeRules.length === 0}
          >
            <ExportIcon />
          </Button>
        </>
      )}
    </TopToolbar>
  );
};

const BulkDeleteRulesButton = ({
  account,
  allChargeRules = [],
  handleRefresh,
}) => {
  const classes = useDeleteButtonStyles();
  const notify = useNotify();
  const { selectedIds, onUnselectItems } = useListContext();
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Resolve against the full rule set, not useList's paginated `data` slice:
  // selection survives page/filter changes, so off-page ids must still delete.
  const rulesById = useMemo(
    () => new Map(allChargeRules.map((rule) => [rule.id, rule])),
    [allChargeRules],
  );

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const ids = selectedIds || [];
      const rules = ids.map((id) => rulesById.get(id)).filter(Boolean);
      const { deleted, failed } = await softDeleteChargeRules(rules);
      const unresolved = ids
        .filter((id) => !rulesById.has(id))
        .map((id) => ({ rule: { id }, reason: new Error("Rule not found") }));
      const allFailed = [...failed, ...unresolved];

      if (deleted.length)
        logHistory({
          parentId: account.id,
          parentType: ResourceType.ACCOUNT,
          text:
            deleted.length === 1
              ? `Billing Rule ${getChargeRuleLabel(deleted[0])} deleted.`
              : `${deleted.length} Billing Rules deleted: ${deleted
                  .map(getChargeRuleLabel)
                  .join("; ")}.`,
        });

      if (allFailed.length) {
        notify(
          `${deleted.length} rule(s) deleted, ${allFailed.length} failed`,
          "warning",
        );
        logError(
          "ChargeRulesTab:BulkDeleteRulesButton",
          new Error(
            `Failed to delete ${allFailed.length} billing rule(s): ` +
              allFailed
                .map(
                  ({ rule, reason }) =>
                    `${rule?.id || "(no id)"}: ${reason?.message || reason}`,
                )
                .join("; "),
          ),
          false,
        );
      } else {
        notify(
          `${deleted.length} rule${deleted.length === 1 ? "" : "s"} deleted`,
          "success",
        );
      }

      onUnselectItems();
      handleRefresh();
    } catch (error) {
      logError("ChargeRulesTab:BulkDeleteRulesButton", error, notify);
    } finally {
      setIsDeleting(false);
      setOpen(false);
    }
  };

  return (
    <>
      <Button
        label="Delete"
        size="small"
        disabled={isDeleting || isSubscriptionDisabled()}
        onClick={() => setOpen(true)}
        className={classnames("ra-delete-button", classes.deleteButton)}
      >
        <DeleteIcon />
      </Button>
      <Confirm
        isOpen={open}
        loading={isDeleting}
        title="Delete Confirmation"
        content={`Delete ${selectedIds.length} Billing Rule${
          selectedIds.length === 1 ? "" : "s"
        }? They will no longer be available for new charges. Charges already added to open shipments will still be billed.`}
        onConfirm={handleDelete}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const PaginationToolbar = (props) => {
  const listContext = useListContext(props);

  return (
    <Grid container direction="row" spacing={1} alignItems="center">
      <Grid item xs={4}>
        <Typography>
          {listContext?.selectedIds?.length > 0 ? (
            `${listContext.selectedIds.length} selected`
          ) : (
            <>&nbsp;</>
          )}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Pagination {...props} {...listContext} />
      </Grid>
    </Grid>
  );
};

const EditButton = (props) => {
  return (
    <Button label="Edit" size="small" color="primary">
      <EditIcon />
    </Button>
  );
};

const fetchTabData = async (
  account,
  setAllChargeRules,
  setChargeRules,
  setEquipmentChoices,
  setLoading,
  notify,
) => {
  try {
    setLoading && setLoading(true);
    const rulesPromise = fetchChargeRules(account.id);
    const equipmentPromise = fetchCarrierEquipmentTypes();

    const [rulesResp, equipmentResp] = await Promise.all([
      rulesPromise,
      equipmentPromise,
    ]);

    setAllChargeRules(rulesResp);
    setChargeRules(rulesResp);
    setEquipmentChoices([
      ...equipmentResp.railEquipmentTypes,
      ...equipmentResp.truckEquipmentTypes,
    ]);
  } catch (error) {
    logError("ChargeRulesTab:getChargeRules", error, notify);
  } finally {
    setLoading && setLoading(false);
  }
};

// Rank plus a warning when this rule would fail validation against the rest
// of the account (ambiguous overlap, duplicate, conflict). Rule numbers in the
// message match this column because both index into allChargeRules.
const RowNumberField = ({ record, allChargeRules = [] }) => {
  const index = allChargeRules.indexOf(record) || 0;
  const issue = record ? validateChargeRule(record, allChargeRules) : {};
  return (
    <FunctionField
      label=""
      render={() => (
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          {index + 1}
          {issue?._error && (
            <Tooltip title={issue._error} placement="right" arrow>
              <WarningIcon
                color="error"
                fontSize="small"
                style={{ marginLeft: 4 }}
              />
            </Tooltip>
          )}
        </span>
      )}
    />
  );
};

const ChargeRulesGrid = (props) => {
  const lgDown = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const {
    account,
    allChargeRules,
    allServices,
    carrierEquipmentChoices,
    history,
    backPath,
    formState,
    openConfirm,
    xsDown,
  } = props;

  const ServiceRateRenderer = (record, allServices) => {
    const service = allServices.find((s) => s.id === record.serviceId) || "";
    const rate = getUnitPrice(service, record.billingUOM);
    const price = formatCurrency(rate);
    return price + " / " + record.billingUOM;
  };

  const handleEdit = (jsonRecord, basePath, record) => {
    if (formState.dirty)
      openConfirm(`/chargeRules/${record.id}`, { account, allChargeRules });
    else
      deepRedirect(history, `/chargeRules/${record.id}`, backPath, {
        account,
        allChargeRules,
      });
  };

  return xsDown ? (
    <SimpleList
      primaryText={(record) => record.event}
      secondaryText={(record) => record.service?.name || record.serviceId}
      tertiaryText={(record) => record.billingUOM}
      leftIcon={(record) => <ExecutionIcon record={record} />}
      rightIcon={() => (
        <ChevronRightIcon
          fontSize="large"
          color="secondary"
          style={{ marginLeft: 40, marginTop: 10 }}
        />
      )}
      linkType={false}
      rowClick={(record) => handleEdit(null, null, record)}
    />
  ) : lgDown ? (
    <Datagrid rowClick={handleEdit} hasBulkActions>
      <RowNumberField label="Rank" allChargeRules={allChargeRules} />
      <FunctionField
        source="execution"
        label=" "
        render={(record) => <ExecutionIcon record={record} />}
      />
      <SelectField source="event" choices={ChargeEventChoices} label="Event" />
      <ReferenceField
        source="serviceId"
        reference="services"
        label="Service"
        link={false}
      >
        <TextField source="name" />
      </ReferenceField>
      <FunctionField
        source="billingUOM"
        label="Rate"
        render={(record) => ServiceRateRenderer(record, allServices)}
      />
      <SelectField
        source="carrierType"
        choices={CarrierTypes}
        label="Carrier Type"
      />
      <ReferenceField
        source="reloadYardCommodityId"
        reference="reloadYardCommoditys"
        label="Load Type"
        link={false}
      >
        <TextField source="commodity.name" />
      </ReferenceField>
      <ReferenceField
        source="areaId"
        reference="areas"
        label="Area"
        link={false}
      >
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField
        source="zoneId"
        reference="zones"
        label="Zone"
        link={false}
      >
        <TextField source="name" />
      </ReferenceField>
    </Datagrid>
  ) : (
    <Datagrid rowClick={handleEdit} hasBulkActions>
      <RowNumberField label="Rank" allChargeRules={allChargeRules} />
      <FunctionField
        source="execution"
        label="Execution"
        render={(record) => <ExecutionIcon record={record} />}
      />
      <SelectField source="event" choices={ChargeEventChoices} label="Event" />
      <ReferenceField
        source="serviceId"
        reference="services"
        label="Service"
        link={false}
      >
        <TextField source="name" />
      </ReferenceField>
      <FunctionField
        source="billingUOM"
        label="Rate"
        render={(record) => ServiceRateRenderer(record, allServices)}
      />
      <SelectField
        source="carrierType"
        label="Carrier Type"
        choices={CarrierTypes}
      />
      <SelectField
        source="carrierEquipmentTypeId"
        label="Equipment Type"
        choices={carrierEquipmentChoices}
      />
      <ReferenceField
        source="reloadYardCommodityId"
        reference="reloadYardCommoditys"
        label="Load Type"
        link={false}
      >
        <TextField source="commodity.name" />
      </ReferenceField>
      <ReferenceField
        source="areaId"
        reference="areas"
        label="Area"
        link={false}
      >
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField
        source="zoneId"
        reference="zones"
        label="Zone"
        link={false}
      >
        <TextField source="name" />
      </ReferenceField>
      <SelectField
        source="frequency"
        choices={StorageFrequencyChoices}
        label="Storage Frequency"
      />
      <SelectField
        source="feeAccrual"
        choices={FeeAccrualChoices}
        label="Storage Accrual"
      />
      <FunctionField
        label="Free Days"
        render={(record) =>
          record?.freeDaysPolicy === FreeDaysPolicy.END_OF_MONTH
            ? "EOM"
            : record?.freeDays
        }
      />
      <NumberField source="freeCars" label="Free Railcars" />
      <NumberField
        source="minCharge"
        label="Min $"
        options={{ maximumFractionDigits: 2 }}
      />
      <NumberField
        source="maxCharge"
        label="Max $"
        options={{ maximumFractionDigits: 2 }}
      />
      <EditButton {...props} />
    </Datagrid>
  );
};

export const ChargeRulesTab = ({
  account,
  allChargeRules,
  setAllChargeRules,
  allServices,
  backPath,
  ...props
}) => {
  const formState = useFormState({ subscription: { dirty: true } });
  const notify = useNotify();
  const history = useHistory();
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const [chargeRules, setChargeRules] = useState([]);
  const [carrierEquipmentChoices, setCarrierEquipmentChoices] = useState([
    ...getRailEquipmentTypes(),
    ...getTruckEquipmentTypes(),
  ]);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmState, setConfirmState] = useState({});
  const defaultSort = { field: "event", order: "ASC" };
  // const fixedFilter = { active: "true" };

  // memorize perPage settings
  // see useEffect [listContext?.perPage]) below
  const sessionPerPage = sessionStorage.getItem(`ChargeRulesTab`);
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? Number(sessionPerPage) : 10,
  );

  useEffect(() => {
    fetchTabData(
      account,
      setAllChargeRules,
      setChargeRules,
      setCarrierEquipmentChoices,
      setLoading,
      notify,
    );
  }, []);

  const handleRefresh = () => {
    fetchTabData(
      account,
      setAllChargeRules,
      setChargeRules,
      setCarrierEquipmentChoices,
      null,
      notify,
    );
  };

  const listContext = useList({
    ...props,
    data: chargeRules || [],
    ids: chargeRules?.map(({ id }) => id) || [],
    loading: loading,
    loaded: !loading || allChargeRules?.length > 0, // renders loading bars if false
    // filter: fixedFilter, // filtered in query
    sort: defaultSort,
    perPage: defaultPerPage,
    basePath: "/chargeRules",
    resource: "chargeRules",
  });

  useEffect(() => {
    if (listContext?.perPage) {
      sessionStorage.setItem(`ChargeRulesTab`, listContext.perPage);
      setDefaultPerPage(Number(listContext.perPage));

      // When perPage changes go back to page 1
      if (listContext.page === 1) return;
      listContext.setPage?.(1);
    }
  }, [listContext?.perPage]);

  const openConfirm = (redirectPath, state) => {
    setConfirmState({ redirectPath, state });
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    deepRedirect(
      history,
      confirmState.redirectPath,
      backPath,
      confirmState.state,
    );
  };

  return (
    <ListContextProvider value={listContext}>
      <ListToolbar
        {...props}
        filters={getListFilters(allServices, xsDown)}
        actions={
          <ListActions
            account={account}
            allChargeRules={allChargeRules}
            allServices={allServices}
            history={history}
            formState={formState}
            openConfirm={openConfirm}
            xsDown={xsDown}
            handleRefresh={handleRefresh}
          />
        }
      />
      <BulkActionsToolbar>
        <BulkDeleteRulesButton
          account={account}
          allChargeRules={allChargeRules}
          handleRefresh={handleRefresh}
        />
      </BulkActionsToolbar>
      <ChargeRulesGrid
        {...props}
        account={account}
        allChargeRules={allChargeRules}
        allServices={allServices}
        carrierEquipmentChoices={carrierEquipmentChoices}
        backPath={backPath}
        history={history}
        formState={formState}
        openConfirm={openConfirm}
        xsDown={xsDown}
      />
      <PaginationToolbar {...props} />
      <Confirm
        isOpen={confirmOpen}
        title="Save Confirmation"
        content="You have un-saved Account changes. If you continue, all changes will be lost. Proceed?"
        onConfirm={() => handleConfirm()}
        onClose={() => setConfirmOpen(false)}
      />
    </ListContextProvider>
  );
};
